<template>
  <div id="TestInvoiceMultiple" style="display: none">
    <div class="invoice-container" style="padding: 10px; font-family: Arial, sans-serif; width: 100%; max-width: none;">
      <!-- Header -->
      <div class="invoice-header" style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
        <h1 style="margin: 0; color: #333;">{{ $t('lab.test-invoice') }}</h1>
        <p style="margin: 5px 0; color: #666;">{{ $brand.brandNameEn }}</p>
        <p style="margin: 5px 0; color: #666;">{{ $t('common.date') }}: {{ getCurrentDate() }}</p>
      </div>

      <!-- Patient Information -->
      <div class="patient-info" style="margin-bottom: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
        <h3 style="margin: 0 0 15px 0; color: #333; border-bottom: 1px solid #dee2e6; padding-bottom: 10px;">
          {{ $t('common.patient_information') }}
        </h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px; width: 25%; font-weight: bold;">{{ $t('common.name') }}:</td>
            <td style="padding: 8px; width: 25%;">{{ getNestedProperty(patientData, 'name') }}</td>
            <td style="padding: 8px; width: 25%; font-weight: bold;">{{ $t('reception.gender') }}:</td>
            <td style="padding: 8px; width: 25%;">{{ getNestedProperty(patientData, 'gender') }}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">{{ $t('reception.birth-date') }}:</td>
            <td style="padding: 8px;">{{ getNestedProperty(patientData, 'birth_date') }}</td>
            <td style="padding: 8px; font-weight: bold;">{{ $t('reception.phone') }}:</td>
            <td style="padding: 8px;">{{ getNestedProperty(patientData, 'phone') }}</td>
          </tr>
        </table>
      </div>

      <!-- Test Details Table -->
      <div class="test-details" style="margin-bottom: 30px;">
        <h3 style="margin: 0 0 15px 0; color: #333; border-bottom: 1px solid #dee2e6; padding-bottom: 10px;">
          {{ $t('lab.test-details') }}
        </h3>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #dee2e6; table-layout: fixed;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="padding: 8px; text-align: left; border: 1px solid #dee2e6; width: 8%;">{{ $t('common.no') }}</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #dee2e6; width: 15%;">{{ $t('common.invoice-no') }}</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #dee2e6; width: 50%;">{{ $t('lab.test-name') }}</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #dee2e6; width: 27%;">{{ $t('employee.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(invoice, invoiceIndex) in invoices">
              <tr v-for="(item, itemIndex) in getInvoiceItems(invoice)" :key="`${invoice.id}-${itemIndex}`">
                <td style="padding: 8px; border: 1px solid #dee2e6;">
                  <span v-if="itemIndex === 0">{{ invoiceIndex + 1 }}</span>
                </td>
                <td style="padding: 8px; border: 1px solid #dee2e6;">
                  <span v-if="itemIndex === 0">#{{ getNestedProperty(invoice, 'invoice_no') }}</span>
                </td>
                <td style="padding: 8px; border: 1px solid #dee2e6;">{{ getNestedProperty(item, 'name', 'N/A') }}</td>
                <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6; font-weight: bold;">
                  {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                </td>
              </tr>
              <tr v-if="getNestedProperty(invoice, 'discount', 0) > 0" :key="`discount-${invoice.id}`" style="background-color: #fff3cd;">
                <td colspan="3" style="padding: 8px; text-align: right; border: 1px solid #dee2e6; font-weight: bold; color: #856404;">
                  {{ $t('common.discount') }} (#{{ getNestedProperty(invoice, 'invoice_no') }}):
                </td>
                <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6; font-weight: bold; color: #856404;">
                  - {{ formatPrice(getNestedProperty(invoice, 'discount', 0)) }}
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr style="background-color: #d4edda; font-weight: bold;">
              <td colspan="3" style="padding: 8px; text-align: right; border: 1px solid #dee2e6;">
                {{ $t('common.total') }}:
              </td>
              <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6; font-size: 1.2em; color: #155724;">
                {{ formatPrice(getTotalAmount()) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Summary -->
      <div class="summary" style="margin-bottom: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px; font-weight: bold;">{{ $t('common.total-invoices') }}:</td>
            <td style="padding: 8px; text-align: right;">{{ invoices.length }}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">{{ $t('lab.total-tests') }}:</td>
            <td style="padding: 8px; text-align: right;">{{ getTotalItemsCount() }}</td>
          </tr>
          <tr v-if="getTotalDiscount() > 0">
            <td style="padding: 8px; font-weight: bold; color: #856404;">{{ $t('common.total-discount') }}:</td>
            <td style="padding: 8px; text-align: right; font-weight: bold; color: #856404;">{{ formatPrice(getTotalDiscount()) }}</td>
          </tr>
          <tr style="font-size: 1.2em; color: #155724;">
            <td style="padding: 8px; font-weight: bold;">{{ $t('common.total-amount') }}:</td>
            <td style="padding: 8px; text-align: right; font-weight: bold;">{{ formatPrice(getTotalAmount()) }}</td>
          </tr>
        </table>
      </div>

      <!-- Payment Status -->
      <div class="payment-status" style="margin-bottom: 30px; padding: 15px; background-color: #d4edda; border-radius: 5px; text-align: center;">
        <h3 style="margin: 0; color: #155724;">
          {{ $t('common.status') }}: APPROVED
        </h3>
      </div>

      <!-- Footer -->
      <div class="invoice-footer" style="margin-top: 50px; text-align: center; padding-top: 20px; border-top: 1px solid #dee2e6; color: #666; font-size: 0.9em;">
        <p style="margin: 5px 0;">{{ $t('common.thank-you') }}</p>
        <p style="margin: 5px 0;">{{ $t('common.printed-on') }}: {{ getCurrentDate() }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getNestedProperty } from "@/utils/nestedProperty.js";

export default {
  props: {
    patientData: {
      type: Object,
      default: () => ({}),
    },
    invoices: {
      type: Array,
      default: () => [],
    },
    testMap: {
      type: Object,
      default: () => ({}),
    },
  },

  methods: {
    getNestedProperty,

    getInvoiceItems(invoice) {
      return getNestedProperty(invoice, 'invoice_items', []);
    },

    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    getTotalItemsCount() {
      return this.invoices.reduce((total, invoice) => {
        const items = getNestedProperty(invoice, 'invoice_items', []);
        return total + items.length;
      }, 0);
    },

    getTotalDiscount() {
      return this.invoices.reduce((total, invoice) => {
        const discount = parseFloat(getNestedProperty(invoice, "discount", 0)) || 0;
        return total + discount;
      }, 0);
    },

    getTotalAmount() {
      return this.invoices.reduce((total, invoice) => {
        const amount = parseFloat(getNestedProperty(invoice, "amount", 0)) || 0;
        const discount = parseFloat(getNestedProperty(invoice, "discount", 0)) || 0;
        return total + (amount - discount);
      }, 0);
    },

    getCurrentDate() {
      return new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
@media print {
  #TestInvoiceMultiple {
    display: block !important;
  }
  
  body {
    margin: 0;
    padding: 0;
  }
  
  .invoice-container {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 5px !important;
  }
  
  table {
    width: 100% !important;
    font-size: 12px !important;
  }
  
  th, td {
    padding: 6px !important;
    font-size: 12px !important;
  }
}
</style>

